import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { mergeReturnDays } from './stickinessCurve';

const AXIS_COLOR = '#7a8499';

// Every window starts at day 1 and extends further out, so the drop from
// day 1 stays visible however far the chart is extended. Ends match the
// Stickiness report's return-rate windows.
const COHORTS = [
  { key: 'd1_7',  label: 'D1–7',  from: 1, to: 7 },
  { key: 'd1_14', label: 'D1–14', from: 1, to: 14 },
  { key: 'd1_30', label: 'D1–30', from: 1, to: 30 },
  { key: 'd1_60', label: 'D1–60', from: 1, to: 60 },
];

const SERIES = [
  { key: 'players',              label: 'Active players', summary: 'Active' },
  { key: 'first_return_players', label: 'First return',   summary: 'First returns' },
];

const CHART_TYPES = [
  { key: 'bar',  label: 'Bars' },
  { key: 'line', label: 'Lines' },
];

function fmtInt(v) { return Number(v).toLocaleString('en-GB'); }
// Later days can sit well under 1% of the day-0 cohort, so whole-number rounding would
// collapse every tick and label to "0%"/"1%".
function pctDecimals(v) {
  const abs = Math.abs(v);
  if (abs === 0 || abs >= 10) return 0;
  return abs >= 1 ? 1 : 2;
}
function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(pctDecimals(Number(v)))}%`; }

function ReturnTooltip({ active, payload, games }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] min-w-[220px]">
      <p className="font-bold text-ink mb-1">Day {row.day} <span className="text-ink-dim font-medium">· % of day-0 players · count</span></p>
      {games.map(g => (
        <div key={g.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
          <span className="text-ink-dim truncate">{g.title}</span>
          <span className="text-ink ml-auto pl-2">{fmtPct(row[g.key])}</span>
          <span className="text-ink-dim w-12 text-right">{fmtInt(row[`${g.key}_n`])}</span>
        </div>
      ))}
    </div>
  );
}

const CHART_MARGIN = { top: 4, right: 16, bottom: 4, left: 4 };

// Grid, axes, tooltip and legend shared by the bar and line variants. Returned
// as an array (not a component) because recharts only picks up its own
// element types as direct children of the chart.
function chartChrome(games) {
  return [
    <CartesianGrid key="grid" vertical={false} stroke="rgba(255,255,255,0.06)" />,
    <XAxis key="x" dataKey="day" stroke={AXIS_COLOR} fontSize={11} interval="preserveStartEnd" />,
    <YAxis key="y" tickFormatter={fmtPct} domain={[0, 'auto']} stroke={AXIS_COLOR} fontSize={11} width={48} />,
    <Tooltip key="tip" content={<ReturnTooltip games={games} />} cursor={{ fill: 'rgba(255,255,255,0.04)', stroke: 'rgba(255,255,255,0.15)' }} />,
    games.length > 1 && (
      <Legend key="legend" wrapperStyle={{ fontSize: 11 }} formatter={v => <span className="text-ink-dim">{v}</span>} />
    ),
  ];
}

// All selected games' returning players over a cohort window in one chart,
// each as % of that game's own Day-0 cohort (everyone who made their first
// bet on it) so games of different sizes share a scale. The series toggle picks everyone active that day, or only players
// for whom it was their first day back after Day 0.
// pointsByKey maps each game's key to its stickiness-curve points,
// cohortByKey to its Day-0 cohort size.
export default function ReturnDaysSection({ games, pointsByKey, cohortByKey }) {
  const [cohortKey, setCohortKey] = useState(COHORTS[0].key);
  const [seriesKey, setSeriesKey] = useState(SERIES[0].key);
  const [chartType, setChartType] = useState(CHART_TYPES[0].key);
  const cohort = COHORTS.find(c => c.key === cohortKey);
  const series = SERIES.find(s => s.key === seriesKey);

  const rows = useMemo(() => mergeReturnDays(
    Object.fromEntries(games.map(g => [g.key, { points: pointsByKey[g.key] || [], cohortPlayers: cohortByKey[g.key] || 0 }])),
    cohort.from, cohort.to, seriesKey,
  ), [games, pointsByKey, cohortByKey, cohort, seriesKey]);

  const hasData = rows.some(r => games.some(g => r[`${g.key}_n`] > 0));
  const first = rows[0];
  const last = rows[rows.length - 1];

  return (
    <div className="mt-6">
      <p className="text-[11px] font-bold uppercase tracking-wide text-ink-dim mb-2">
        Returning players as % of day-0 players — {series.label.toLowerCase()}, {cohort.label}
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <ToggleGroup value={seriesKey} onChange={setSeriesKey}>
          {SERIES.map(s => <ToggleItem key={s.key} value={s.key}>{s.label}</ToggleItem>)}
        </ToggleGroup>
        <ToggleGroup value={cohortKey} onChange={setCohortKey}>
          {COHORTS.map(c => <ToggleItem key={c.key} value={c.key}>{c.label}</ToggleItem>)}
        </ToggleGroup>
        <ToggleGroup value={chartType} onChange={setChartType}>
          {CHART_TYPES.map(t => <ToggleItem key={t.key} value={t.key}>{t.label}</ToggleItem>)}
        </ToggleGroup>
      </div>
      {hasData && first && last && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2 text-[11px] text-ink-dim">
          <span>{series.summary}, day 1 → day {cohort.to}:</span>
          {games.map(g => (
            <span key={g.key} className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
              {games.length > 1 && <span>{g.title}</span>}
              <span className="text-ink font-bold">{fmtPct(first[g.key])} → {fmtPct(last[g.key])}</span>
              <span>of {fmtInt(cohortByKey[g.key] || 0)}</span>
            </span>
          ))}
        </div>
      )}
      <div className="h-[280px]">
        {!hasData ? (
          <div className="flex items-center justify-center h-full text-xs text-ink-dim">
            No returning players on days {cohort.from}–{cohort.to}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart data={rows} margin={CHART_MARGIN}>
                {chartChrome(games)}
                {games.map(g => (
                  <Line
                    key={g.key} type="monotone" dataKey={g.key} name={g.title} stroke={g.color} strokeWidth={2}
                    dot={cohort.to <= 14 ? { r: 3 } : false} activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            ) : (
              <BarChart data={rows} margin={CHART_MARGIN}>
                {chartChrome(games)}
                {games.map(g => (
                  <Bar key={g.key} dataKey={g.key} name={g.title} fill={g.color} radius={[2, 2, 0, 0]} maxBarSize={28} />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
